import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { UserSearchSelect } from "../ui/UserSearchSelect";
import { EmptyState } from "../shared/EmptyState";
import { toast } from "sonner";
import { Users, UserPlus, UserMinus, ArrowRightLeft, Clock, CheckCircle, XCircle, Trash2, Phone, Mail, Briefcase } from "lucide-react";

const TYPE_CONFIG = {
    add_member: { label: "Ajout", icon: UserPlus, color: "bg-emerald-100 text-emerald-700" },
    remove_member: { label: "Retrait", icon: UserMinus, color: "bg-red-100 text-red-700" },
    transfer_member: { label: "Transfert", icon: ArrowRightLeft, color: "bg-blue-100 text-blue-700" },
};

const STATUS_CONFIG = {
    pending: { label: "En attente", color: "bg-amber-100 text-amber-700", icon: Clock },
    approved: { label: "Approuvée", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
    rejected: { label: "Refusée", color: "bg-red-100 text-red-700", icon: XCircle },
};

export function TeamManagement() {
    const { currentUser, users, departments, supervisedDepartments, isTeamSupervisor, getUserById, hasPermission, refreshUsers } = useApp();
    const isITStaff = hasPermission("agent");
    const [positions, setPositions] = useState([]);
    const [myRequests, setMyRequests] = useState([]);
    const [loading, setLoading] = useState(true);

    // Dialogs
    const [addOpen, setAddOpen] = useState(false);
    const [addDeptId, setAddDeptId] = useState("");
    const [addTab, setAddTab] = useState("existing"); // "existing" | "new"
    const [addUserId, setAddUserId] = useState(null);
    const [addPositionIds, setAddPositionIds] = useState([]);
    const [addReason, setAddReason] = useState("");
    const [newFirst, setNewFirst] = useState("");
    const [newLast, setNewLast] = useState("");
    const [newPhone, setNewPhone] = useState("");
    const [newEmail, setNewEmail] = useState("");

    const [removeOpen, setRemoveOpen] = useState(false);
    const [removeUser, setRemoveUser] = useState(null);
    const [removeDeptId, setRemoveDeptId] = useState("");
    const [removeReason, setRemoveReason] = useState("");

    const [transferOpen, setTransferOpen] = useState(false);
    const [transferUser, setTransferUser] = useState(null);
    const [transferFromDept, setTransferFromDept] = useState("");
    const [transferToDept, setTransferToDept] = useState("");
    const [transferPositionIds, setTransferPositionIds] = useState([]);
    const [transferReason, setTransferReason] = useState("");

    const [submitting, setSubmitting] = useState(false);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const [pos, reqs] = await Promise.all([
                api.getPositions(),
                api.getTeamRequests(),
            ]);
            setPositions(pos);
            setMyRequests(reqs);
        } catch {
            toast.error("Erreur de chargement");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    // Group department members
    const getDeptMembers = useCallback((deptId) => {
        return users.filter((u) => {
            if (u.is_active === false) return false;
            const allDepts = [u.department_id, ...(u.secondary_department_ids || [])].filter(Boolean);
            return allDepts.includes(deptId);
        });
    }, [users]);

    const getPositionNames = useCallback((posIds) => {
        return (posIds || []).map((pid) => positions.find((p) => p.id === pid)?.name).filter(Boolean).join(", ");
    }, [positions]);

    const allDepts = useMemo(() => departments.filter((d) => !d.deleted_at), [departments]);

    // Departments to display: admins/agents see all, supervisors see only their departments
    const visibleDepts = useMemo(() => {
        if (isITStaff) return allDepts;
        return supervisedDepartments;
    }, [isITStaff, allDepts, supervisedDepartments]);

    // Reset dialogs
    const resetAdd = () => { setAddUserId(null); setAddPositionIds([]); setAddReason(""); setNewFirst(""); setNewLast(""); setNewPhone(""); setNewEmail(""); setAddTab("existing"); };
    const resetRemove = () => { setRemoveUser(null); setRemoveReason(""); };
    const resetTransfer = () => { setTransferUser(null); setTransferToDept(""); setTransferPositionIds([]); setTransferReason(""); };

    const handleAdd = async () => {
        setSubmitting(true);
        try {
            if (addTab === "existing") {
                if (!addUserId) { toast.error("Sélectionnez un utilisateur"); setSubmitting(false); return; }
                await api.createTeamRequest({
                    type: "add_member",
                    department_id: addDeptId,
                    target_user_id: addUserId,
                    position_ids: addPositionIds,
                    reason: addReason,
                });
                toast.success("Demande envoyée. En attente de validation par l'administration.");
            } else {
                // Direct creation of external contact
                if (!newFirst.trim() || !newLast.trim()) { toast.error("Prénom et nom requis"); setSubmitting(false); return; }
                await api.createUser({
                    first_name: newFirst.trim(),
                    last_name: newLast.trim().toUpperCase(),
                    phone_mobile: newPhone.trim() || "",
                    email: newEmail.trim() || "",
                    department_id: addDeptId,
                    user_type: "external",
                    role: "user",
                    position_ids: addPositionIds,
                });
                toast.success(`Contact créé : ${newFirst.trim()} ${newLast.trim().toUpperCase()}`);
                if (refreshUsers) refreshUsers();
            }
            setAddOpen(false);
            resetAdd();
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur");
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemove = async () => {
        setSubmitting(true);
        try {
            await api.createTeamRequest({
                type: "remove_member",
                department_id: removeDeptId,
                target_user_id: removeUser?.id,
                reason: removeReason,
            });
            toast.success("Demande envoyée.");
            setRemoveOpen(false);
            resetRemove();
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur");
        } finally {
            setSubmitting(false);
        }
    };

    const handleTransfer = async () => {
        if (!transferToDept) { toast.error("Sélectionnez un département de destination"); return; }
        setSubmitting(true);
        try {
            await api.createTeamRequest({
                type: "transfer_member",
                department_id: transferFromDept,
                target_user_id: transferUser?.id,
                from_department_id: transferFromDept,
                to_department_id: transferToDept,
                position_ids: transferPositionIds,
                reason: transferReason,
            });
            toast.success("Demande envoyée.");
            setTransferOpen(false);
            resetTransfer();
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur");
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = async (reqId) => {
        try {
            await api.cancelTeamRequest(reqId);
            toast.success("Demande annulée");
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur");
        }
    };

    if (loading) return <div className="page-container"><div className="animate-pulse text-gray-400">Chargement...</div></div>;
    if (!isITStaff && !isTeamSupervisor) return <div className="page-container"><EmptyState icon={Users} title={"Accès restreint"} description={"Vous ne supervisez aucun département."} /></div>;

    return (
        <div className="page-container" data-testid="team-management">
            <div className="page-header">
                <h1 className="page-title">{"Mon équipe"}</h1>
                <p className="page-subtitle">{"Gérez les membres de vos départements"}</p>
            </div>

            {/* Department sections */}
            {visibleDepts.map((dept) => {
                const members = getDeptMembers(dept.id);
                const isMgr = dept.manager_id === currentUser?.id;
                return (
                    <div key={dept.id} className="card mb-6" data-testid={`dept-section-${dept.id}`}>
                        <div className="card-header flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Users className="w-5 h-5 text-teal-600" />
                                <h2 className="text-sm font-semibold text-gray-900">{dept.name}</h2>
                                {isMgr ? (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">Responsable</span>
                                ) : (dept.assistant_ids || []).includes(currentUser?.id) ? (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Assistante</span>
                                ) : isITStaff ? (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Administration</span>
                                ) : null}
                            </div>
                            <Button size="sm" variant="outline" onClick={() => { setAddDeptId(dept.id); resetAdd(); setAddOpen(true); }} data-testid={`add-member-btn-${dept.id}`}>
                                <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Ajouter
                            </Button>
                        </div>
                        <div className="card-body p-0">
                            {members.length === 0 ? (
                                <p className="text-sm text-gray-400 px-4 py-6 text-center">Aucun membre</p>
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {members.map((m) => (
                                        <div key={m.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 group" data-testid={`member-row-${m.id}`}>
                                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                                                {(m.first_name?.[0] || "")}{(m.last_name?.[0] || "")}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                {m.first_name} {m.last_name}
                                                {m.user_type === "external" && <span className="inline-flex items-center ml-1.5 px-1 py-0 rounded text-[9px] font-semibold bg-amber-100 text-amber-700">EXT</span>}
                                            </p>
                                                <div className="flex items-center gap-3 text-xs text-gray-500">
                                                    {getPositionNames(m.position_ids) && <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{getPositionNames(m.position_ids)}</span>}
                                                    {m.phone_mobile && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{m.phone_mobile}</span>}
                                                    {m.email && !m.email.includes("@no-email") && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{m.email}</span>}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                {m.id !== dept.manager_id && (
                                                    <>
                                                        <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-800 h-7 px-2" onClick={() => { setTransferUser(m); setTransferFromDept(dept.id); resetTransfer(); setTransferOpen(true); }} data-testid={`transfer-btn-${m.id}`}>
                                                            <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> {"Transférer"}
                                                        </Button>
                                                        <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-800 h-7 px-2" onClick={() => { setRemoveUser(m); setRemoveDeptId(dept.id); setRemoveReason(""); setRemoveOpen(true); }} data-testid={`remove-btn-${m.id}`}>
                                                            <UserMinus className="w-3.5 h-3.5 mr-1" /> Retirer
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}

            {/* My requests section */}
            <div className="card" data-testid="my-requests-section">
                <div className="card-header">
                    <h2 className="text-sm font-semibold text-gray-900">Mes demandes</h2>
                </div>
                <div className="card-body p-0">
                    {myRequests.length === 0 ? (
                        <p className="text-sm text-gray-400 px-4 py-6 text-center">Aucune demande</p>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {myRequests.map((req) => {
                                const tc = TYPE_CONFIG[req.type] || {};
                                const sc = STATUS_CONFIG[req.status] || {};
                                const TypeIcon = tc.icon || Clock;
                                const StatusIcon = sc.icon || Clock;
                                return (
                                    <div key={req.id} className="flex items-center gap-3 px-4 py-3" data-testid={`request-row-${req.id}`}>
                                        <span className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${tc.color}`}>
                                            <TypeIcon className="w-3 h-3" /> {tc.label}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-gray-900 truncate">
                                                {req.target_user_name || "Nouveau contact"} — {req.department_name}
                                                {req.type === "transfer_member" && req.to_department_name && <span className="text-gray-500"> → {req.to_department_name}</span>}
                                            </p>
                                            {req.reason && <p className="text-xs text-gray-500 truncate">{req.reason}</p>}
                                            {req.status === "rejected" && req.rejection_reason && (
                                                <p className="text-xs text-red-600 mt-0.5">Motif du refus : {req.rejection_reason}</p>
                                            )}
                                        </div>
                                        <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${sc.color}`}>
                                            <StatusIcon className="w-3 h-3" /> {sc.label}
                                        </span>
                                        {req.status === "pending" && (
                                            <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-600 h-7 px-2" onClick={() => handleCancel(req.id)} data-testid={`cancel-req-${req.id}`}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* ========== ADD MEMBER DIALOG ========== */}
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogContent className="max-w-lg" data-testid="add-member-dialog">
                    <DialogHeader>
                        <DialogTitle>Ajouter un membre</DialogTitle>
                    </DialogHeader>
                    <div className="flex gap-1 bg-gray-100 p-1 rounded-lg mb-4">
                        <button onClick={() => setAddTab("existing")} className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${addTab === "existing" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"}`} data-testid="tab-existing-user">
                            Utilisateur existant
                        </button>
                        <button onClick={() => setAddTab("new")} className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${addTab === "new" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"}`} data-testid="tab-new-contact">
                            Nouveau contact
                        </button>
                    </div>
                    {addTab === "existing" ? (
                        <div className="space-y-3">
                            <div>
                                <label className="form-label text-xs">Utilisateur</label>
                                <UserSearchSelect
                                    users={users.filter((u) => u.is_active !== false)}
                                    departments={departments}
                                    value={addUserId}
                                    onChange={setAddUserId}
                                    placeholder="Rechercher un utilisateur..."
                                    data-testid="add-user-select"
                                />
                            </div>
                            <div>
                                <label className="form-label text-xs">Poste(s)</label>
                                <div className="flex flex-wrap gap-1.5">
                                    {positions.map((p) => (
                                        <button key={p.id} type="button" onClick={() => setAddPositionIds((prev) => prev.includes(p.id) ? prev.filter((x) => x !== p.id) : [...prev, p.id])} className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${addPositionIds.includes(p.id) ? "bg-teal-100 border-teal-300 text-teal-700" : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                                            {p.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="form-label text-xs">{"Prénom *"}</label>
                                    <Input value={newFirst} onChange={(e) => setNewFirst(e.target.value)} placeholder="Jean" data-testid="new-first-name" />
                                </div>
                                <div>
                                    <label className="form-label text-xs">Nom *</label>
                                    <Input value={newLast} onChange={(e) => setNewLast(e.target.value.toUpperCase())} placeholder="DUPONT" data-testid="new-last-name" />
                                </div>
                            </div>
                            <div>
                                <label className="form-label text-xs">Téléphone portable</label>
                                <Input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="+41 79 123 45 67" type="tel" data-testid="new-phone" />
                            </div>
                            <div>
                                <label className="form-label text-xs">Email (optionnel)</label>
                                <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="jean.dupont@example.com" data-testid="new-email" />
                            </div>
                            <div>
                                <label className="form-label text-xs">Poste(s)</label>
                                <div className="flex flex-wrap gap-1.5">
                                    {positions.map((p) => (
                                        <button key={p.id} type="button" onClick={() => setAddPositionIds((prev) => prev.includes(p.id) ? prev.filter((x) => x !== p.id) : [...prev, p.id])} className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${addPositionIds.includes(p.id) ? "bg-teal-100 border-teal-300 text-teal-700" : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                                            {p.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                    {addTab === "existing" && (
                        <div className="mt-3">
                            <label className="form-label text-xs">Motif (optionnel)</label>
                            <textarea value={addReason} onChange={(e) => setAddReason(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-md resize-none" rows={2} placeholder="Nouveau collaborateur, remplacement..." data-testid="add-reason" />
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddOpen(false)}>Annuler</Button>
                        <Button onClick={handleAdd} disabled={submitting} data-testid="submit-add-member">
                            {submitting ? "..." : addTab === "new" ? "Créer le contact" : "Envoyer la demande"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ========== REMOVE MEMBER DIALOG ========== */}
            <Dialog open={removeOpen} onOpenChange={setRemoveOpen}>
                <DialogContent data-testid="remove-member-dialog">
                    <DialogHeader>
                        <DialogTitle>Demander le retrait</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-gray-700">
                        {"Demander le retrait de "}<strong>{removeUser?.first_name} {removeUser?.last_name}</strong>{" du département "}<strong>{departments.find((d) => d.id === removeDeptId)?.name}</strong>{" ?"}
                    </p>
                    <div className="mt-3">
                        <label className="form-label text-xs">Motif (optionnel)</label>
                        <textarea value={removeReason} onChange={(e) => setRemoveReason(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-md resize-none" rows={2} data-testid="remove-reason" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRemoveOpen(false)}>Annuler</Button>
                        <Button variant="destructive" onClick={handleRemove} disabled={submitting} data-testid="submit-remove-member">
                            {submitting ? "Envoi..." : "Envoyer la demande"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ========== TRANSFER MEMBER DIALOG ========== */}
            <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
                <DialogContent className="max-w-lg" data-testid="transfer-member-dialog">
                    <DialogHeader>
                        <DialogTitle>{"Transférer un membre"}</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-gray-700 mb-3">
                        {"Transférer "}<strong>{transferUser?.first_name} {transferUser?.last_name}</strong>{" vers un autre département."}
                    </p>
                    <div className="space-y-3">
                        <div>
                            <label className="form-label text-xs">{"Département de destination"}</label>
                            <Select value={transferToDept} onValueChange={setTransferToDept}>
                                <SelectTrigger data-testid="transfer-dept-select">
                                    <SelectValue placeholder={"Choisir un département"} />
                                </SelectTrigger>
                                <SelectContent>
                                    {allDepts.filter((d) => d.id !== transferFromDept).map((d) => (
                                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="form-label text-xs">{"Poste(s) dans le nouveau département"}</label>
                            <div className="flex flex-wrap gap-1.5">
                                {positions.map((p) => (
                                    <button key={p.id} type="button" onClick={() => setTransferPositionIds((prev) => prev.includes(p.id) ? prev.filter((x) => x !== p.id) : [...prev, p.id])} className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${transferPositionIds.includes(p.id) ? "bg-teal-100 border-teal-300 text-teal-700" : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                                        {p.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="form-label text-xs">Motif (optionnel)</label>
                            <textarea value={transferReason} onChange={(e) => setTransferReason(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-md resize-none" rows={2} data-testid="transfer-reason" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setTransferOpen(false)}>Annuler</Button>
                        <Button onClick={handleTransfer} disabled={submitting || !transferToDept} data-testid="submit-transfer-member">
                            {submitting ? "Envoi..." : "Envoyer la demande"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
